import { Selector, createListCollection } from "@buildblockinc/buildblock-design-system";
import { useMemo } from "react";

export type WeatherCondition =
  | "TYPE_UNSPECIFIED"
  | "CLEAR"
  | "MOSTLY_CLEAR"
  | "PARTLY_CLOUDY"
  | "MOSTLY_CLOUDY"
  | "CLOUDY"
  | "WINDY"
  | "WIND_AND_RAIN"
  | "LIGHT_RAIN_SHOWERS"
  | "CHANCE_OF_SHOWERS"
  | "SCATTERED_SHOWERS"
  | "RAIN_SHOWERS"
  | "HEAVY_RAIN_SHOWERS"
  | "LIGHT_TO_MODERATE_RAIN"
  | "MODERATE_TO_HEAVY_RAIN"
  | "RAIN"
  | "LIGHT_RAIN"
  | "HEAVY_RAIN"
  | "RAIN_PERIODICALLY_HEAVY"
  | "LIGHT_SNOW_SHOWERS"
  | "CHANCE_OF_SNOW_SHOWERS"
  | "SCATTERED_SNOW_SHOWERS"
  | "SNOW_SHOWERS"
  | "HEAVY_SNOW_SHOWERS"
  | "LIGHT_TO_MODERATE_SNOW"
  | "MODERATE_TO_HEAVY_SNOW"
  | "SNOW"
  | "LIGHT_SNOW"
  | "HEAVY_SNOW"
  | "SNOWSTORM"
  | "SNOW_PERIODICALLY_HEAVY"
  | "HEAVY_SNOW_STORM"
  | "BLOWING_SNOW"
  | "RAIN_AND_SNOW"
  | "HAIL"
  | "HAIL_SHOWERS"
  | "THUNDERSTORM"
  | "THUNDERSHOWER"
  | "LIGHT_THUNDERSTORM_RAIN"
  | "SCATTERED_THUNDERSTORMS"
  | "HEAVY_THUNDERSTORM";

type WeatherGroup = "CLEAR" | "CLOUDY" | "RAIN" | "SNOW" | "THUNDERSTORM" | "WINDY";

interface WeatherOption {
  label: string;
  value: WeatherGroup;
}

const WEATHER_OPTIONS: WeatherOption[] = [
  { label: "Sunny", value: "CLEAR" },
  { label: "Cloudy", value: "CLOUDY" },
  { label: "Rain", value: "RAIN" },
  { label: "Snow", value: "SNOW" },
  { label: "Thunderstorm", value: "THUNDERSTORM" },
  { label: "Windy", value: "WINDY" },
];

export const WEATHER_GROUP_MAP: Record<WeatherCondition, WeatherGroup> = {
  TYPE_UNSPECIFIED: "CLEAR",
  CLEAR: "CLEAR",
  MOSTLY_CLEAR: "CLEAR",

  PARTLY_CLOUDY: "CLOUDY",
  MOSTLY_CLOUDY: "CLOUDY",
  CLOUDY: "CLOUDY",

  WINDY: "WINDY",
  WIND_AND_RAIN: "RAIN",
  LIGHT_RAIN_SHOWERS: "RAIN",
  CHANCE_OF_SHOWERS: "RAIN",
  SCATTERED_SHOWERS: "RAIN",
  RAIN_SHOWERS: "RAIN",
  HEAVY_RAIN_SHOWERS: "RAIN",
  LIGHT_TO_MODERATE_RAIN: "RAIN",
  MODERATE_TO_HEAVY_RAIN: "RAIN",
  RAIN: "RAIN",
  LIGHT_RAIN: "RAIN",
  HEAVY_RAIN: "RAIN",
  RAIN_PERIODICALLY_HEAVY: "RAIN",

  LIGHT_SNOW_SHOWERS: "SNOW",
  CHANCE_OF_SNOW_SHOWERS: "SNOW",
  SCATTERED_SNOW_SHOWERS: "SNOW",
  SNOW_SHOWERS: "SNOW",
  HEAVY_SNOW_SHOWERS: "SNOW",
  LIGHT_TO_MODERATE_SNOW: "SNOW",
  MODERATE_TO_HEAVY_SNOW: "SNOW",
  SNOW: "SNOW",
  LIGHT_SNOW: "SNOW",
  HEAVY_SNOW: "SNOW",
  SNOWSTORM: "SNOW",
  SNOW_PERIODICALLY_HEAVY: "SNOW",
  HEAVY_SNOW_STORM: "SNOW",
  BLOWING_SNOW: "SNOW",
  RAIN_AND_SNOW: "SNOW",

  HAIL: "THUNDERSTORM",
  HAIL_SHOWERS: "THUNDERSTORM",
  THUNDERSTORM: "THUNDERSTORM",
  THUNDERSHOWER: "THUNDERSTORM",
  LIGHT_THUNDERSTORM_RAIN: "THUNDERSTORM",
  SCATTERED_THUNDERSTORMS: "THUNDERSTORM",
  HEAVY_THUNDERSTORM: "THUNDERSTORM",
};

export function toWeatherGroup(condition: WeatherCondition): WeatherGroup {
  return WEATHER_GROUP_MAP[condition];
}

interface WeatherSelectorProps {
  value: string;
  onValueChange: (value: WeatherCondition) => void;
}

export function WeatherSelector({ value, onValueChange }: WeatherSelectorProps) {
  const collection = useMemo(() => createListCollection<WeatherOption>({ items: WEATHER_OPTIONS }), []);
  const groupedValue = value ? (WEATHER_GROUP_MAP[value as WeatherCondition] ?? value) : "";

  return (
    <Selector<WeatherOption>
      collection={collection}
      value={groupedValue ? [groupedValue] : []}
      onValueChange={(details) => {
        const selected = details.value[0] as WeatherCondition | undefined;

        if (selected) onValueChange(selected);
      }}
      className="w-full"
      required
    >
      <Selector.Label>Weather</Selector.Label>
      <Selector.Control size="medium" variant="outline">
        {({ selectedItems }) => {
          const selected = selectedItems[0] as WeatherOption | undefined;
          return <span className="truncate">{selected?.label ?? "Select"}</span>;
        }}
      </Selector.Control>
      <Selector.Content className="overflow-y-auto z-10 p-2 max-h-60" usePortal={false}>
        {collection.items.map((item) => (
          <Selector.Item key={item.value} item={item} className="px-3 py-2 cursor-pointer hover:bg-gray-50">
            {({ selected }) => (
              <div className="flex gap-2 justify-between items-center">
                <span className="text-gray-1000 text-label2">{item.label}</span>
                {selected && <span className="text-blue-500">✓</span>}
              </div>
            )}
          </Selector.Item>
        ))}
      </Selector.Content>
    </Selector>
  );
}
